import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Mirrors `description` max_length in portal-backend app/api/v1/instrument_runs.py (InstrumentRunDraftCreate / InstrumentRunDraftUpdate).
export const RUN_DESCRIPTION_MAX_LENGTH = 2048;

// Codepoints, not UTF-16 units: matches Pydantic's len()-based max_length and the Textarea's own counter.
export const isRunDescriptionTooLong = (value: string | undefined): boolean =>
  Array.from(value ?? '').length > RUN_DESCRIPTION_MAX_LENGTH;

// Server errors are strings today; a schema-style `{ message }` issue must not render as "[object Object]".
const errorText = (error: unknown): string | undefined => {
  if (error === undefined) return undefined;
  if (typeof error === 'object' && error !== null && 'message' in error) return String(error.message);
  return String(error);
};

type Props = {
  value: string | undefined;
  onChange: (value: string) => void;
  /** Field errors (e.g. server field errors); the first replaces the Textarea's built-in over-limit message. */
  errors?: readonly unknown[];
};

const RunDescriptionField = ({ value, onChange, errors = [] }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  const formError = errors.find(Boolean);
  const exceededMessage = useCallback((excess: number) => t('description_exceeded', { count: excess }), [t]);

  return (
    <Textarea
      data-testid="run-description-textarea"
      id="run-description"
      error={errorText(formError)}
      label={{
        children: t('description'),
        htmlFor: 'run-description',
        id: 'label-run-description',
      }}
      maxLength={RUN_DESCRIPTION_MAX_LENGTH}
      maxLengthExceededMessage={exceededMessage}
      maxLengthHint={t('description_max_length_hint', { count: RUN_DESCRIPTION_MAX_LENGTH })}
      onChange={e => onChange(e.target.value)}
      showCount
      value={value ?? ''}
    />
  );
};

export default RunDescriptionField;
